import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useAnimationControls } from "framer-motion";
import { Menu, X, ChevronDown, Anchor, MapPin, Tag } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LINKS = [
  { label: "Platform", href: "#platform" },
  { label: "Oceans", href: "#oceans" },
  { label: "Forests", to: "/forests", accent: "emerald" },
  { label: "Agriculture", href: "#agriculture" },
  { label: "Live Map", href: "#livemap" },
  { label: "Process", href: "#process" },
  { label: "Pricing", href: "#pricing" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const logoControls = useAnimationControls();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onArrive = () => {
      logoControls.start({
        scale: [1, 1.4, 1],
        filter: [
          "drop-shadow(0 0 0px rgba(0,229,255,0))",
          "drop-shadow(0 0 18px rgba(0,229,255,0.95))",
          "drop-shadow(0 0 0px rgba(0,229,255,0))",
        ],
        transition: { duration: 1, ease: "easeOut" },
      });
    };
    window.addEventListener("eagle-soar-arrive", onArrive);
    return () => window.removeEventListener("eagle-soar-arrive", onArrive);
  }, [logoControls]);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? "glass" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="container-x flex h-16 items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center">
          <motion.span animate={logoControls} className="flex items-center">
            <Logo className="h-7 w-7" withWordmark />
          </motion.span>
        </Link>

        <div className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) =>
            l.to ? (
              <Link
                key={l.label}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
                className={`text-sm text-slate-300 transition-colors duration-200 ${
                  l.accent === "emerald" ? "hover:text-emerald-400" : "hover:text-arjuna-teal"
                }`}
              >
                {l.label}
              </Link>
            ) : null
          )}
          <Link
            to="/events"
            data-testid="nav-link-events"
            className="relative text-sm text-slate-300 transition-colors duration-200 hover:text-arjuna-teal"
          >
            Events
            <span className="absolute -right-2 -top-1 h-1.5 w-1.5 animate-pulse rounded-full bg-[#FF7A1A]" />
          </Link>
          <Link
            to="/investors"
            data-testid="nav-link-investors"
            className="text-sm text-slate-300 transition-colors duration-200 hover:text-arjuna-teal"
          >
            For Investors
          </Link>
          <Link
            to="/edge"
            data-testid="nav-link-edge"
            className="relative text-sm text-slate-300 transition-colors duration-200 hover:text-arjuna-teal"
          >
            Edge
            <span className="absolute -right-2 -top-1 h-1.5 w-1.5 animate-pulse rounded-full bg-arjuna-teal" />
          </Link>
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            href="https://expeditions.arjunaspace.in"
            target="_blank"
            rel="noreferrer noopener"
            data-testid="nav-expeditions-btn"
            className="group relative inline-flex items-center justify-center overflow-hidden rounded-full border border-red-500/50 bg-red-600 px-6 py-2 text-sm font-semibold text-white shadow-[0_0_24px_rgba(239,68,68,0.35)] transition-all duration-300 hover:border-red-400 hover:bg-red-500 hover:shadow-[0_0_32px_rgba(239,68,68,0.6)]"
          >
            <span className="absolute left-3 top-1/2 h-2 w-2 -translate-y-1/2">
              <span className="absolute inset-0 animate-ping rounded-full bg-white/70" />
              <span className="absolute inset-0 rounded-full bg-white" />
            </span>
            Expeditions
          </a>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="nav-explore-trigger"
                className="flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-sm font-medium text-white transition-colors duration-300 hover:border-arjuna-teal/60 hover:text-arjuna-teal focus:outline-none"
              >
                Explore
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56 border-white/10 bg-arjuna-surface/95 text-white backdrop-blur-xl"
              data-testid="nav-explore-menu"
            >
              <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-widest text-arjuna-teal">
                Sections
              </DropdownMenuLabel>
              <DropdownMenuItem asChild className="cursor-pointer focus:bg-white/10 focus:text-arjuna-teal">
                <a href="#ports" data-testid="nav-explore-ports">
                  <Anchor className="mr-2 h-4 w-4 text-arjuna-teal" /> Ports of India
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="cursor-pointer focus:bg-white/10 focus:text-arjuna-teal">
                <a href="#livemap" data-testid="nav-explore-livemap">
                  <MapPin className="mr-2 h-4 w-4 text-arjuna-teal" /> Live Map
                </a>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem asChild className="cursor-pointer focus:bg-white/10 focus:text-arjuna-teal">
                <a href="#pricing" data-testid="nav-explore-pricing">
                  <Tag className="mr-2 h-4 w-4 text-arjuna-teal" /> Pricing
                </a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <button
              data-testid="nav-dashboard-btn"
              onClick={() => navigate("/dashboard")}
              className="btn-primary"
            >
              Open Console
            </button>
          ) : (
            <>
              <Link to="/login" data-testid="nav-signin-btn" className="btn-ghost">
                Sign in
              </Link>
              <a href="#demo" data-testid="nav-demo-btn" className="btn-primary">
                Request a Demo
              </a>
            </>
          )}
        </div>

        <button
          className="text-white lg:hidden"
          onClick={() => setOpen((o) => !o)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </nav>

      {open && (
        <div className="glass border-t border-white/10 px-6 py-6 lg:hidden" data-testid="nav-mobile-menu">
          <div className="flex flex-col gap-4">
            {LINKS.map((l) =>
              l.to ? (
                <Link
                  key={l.label}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className={`text-slate-200 ${
                    l.accent === "emerald" ? "hover:text-emerald-400" : "hover:text-arjuna-teal"
                  }`}
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="text-slate-200 hover:text-arjuna-teal"
                >
                  {l.label}
                </a>
              )
            )}
            <Link
              to="/events"
              onClick={() => setOpen(false)}
              data-testid="nav-link-events-mobile"
              className="text-slate-200 hover:text-arjuna-teal"
            >
              Events
            </Link>
            <Link
              to="/investors"
              onClick={() => setOpen(false)}
              data-testid="nav-link-investors-mobile"
              className="text-slate-200 hover:text-arjuna-teal"
            >
              For Investors
            </Link>
            <Link
              to="/edge"
              onClick={() => setOpen(false)}
              data-testid="nav-link-edge-mobile"
              className="text-slate-200 hover:text-arjuna-teal"
            >
              Edge
            </Link>
            <a
              href="https://expeditions.arjunaspace.in"
              target="_blank"
              rel="noreferrer noopener"
              onClick={() => setOpen(false)}
              data-testid="nav-expeditions-mobile"
              className="inline-flex items-center gap-2 rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-[0_0_18px_rgba(239,68,68,0.4)]"
            >
              <span className="relative h-2 w-2">
                <span className="absolute inset-0 animate-ping rounded-full bg-white/70" />
                <span className="absolute inset-0 rounded-full bg-white" />
              </span>
              Expeditions
            </a>
            <div className="mt-2 flex gap-3">
              {user ? (
                <button onClick={() => navigate("/dashboard")} className="btn-primary flex-1">
                  Console
                </button>
              ) : (
                <>
                  <Link to="/login" className="btn-ghost flex-1">
                    Sign in
                  </Link>
                  <a href="#demo" onClick={() => setOpen(false)} className="btn-primary flex-1">
                    Demo
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </motion.header>
  );
}
